import { forwardRef, useLayoutEffect, useRef } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'
import TextField from '@mui/material/TextField'
import type { TextFieldProps } from '@mui/material/TextField'

import {
  countDigitsBefore,
  offsetAfterDigits,
  toCanonicalNumber,
  toDisplayNumber,
} from '../../numerals'

type Passthrough = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'type' | 'inputMode' | 'inputRef'
>

export interface NumericFieldProps extends Passthrough {
  /** The canonical value: ASCII digits, no separators. '' means nothing entered. */
  value: string
  /** Called with the canonical value. Never with what is on screen. */
  onChange: (canonical: string) => void
  allowNegative?: boolean
  decimals?: number
  grouping?: boolean
}

/**
 * A number the operator reads in Persian and the server receives in ASCII.
 *
 * `type='number'` is not used, and cannot be: a native number input owns its
 * own text, so it will not display «۱۲٬۵۰۰٬۰۰۰», it silently discards a value
 * it dislikes rather than showing what was typed, and its spinner invites
 * arithmetic on a figure that is not a quantity. This is a text control with
 * explicit normalisation, which is the more correct instrument.
 *
 * The caret is the hard part. Formatting rewrites the string under the cursor,
 * so a naive implementation parks it at the end after every keystroke and
 * makes the middle of a number uneditable. Here the position is remembered as
 * *how many digits precede it* — separators come and go, digits do not — and
 * restored against the same count in the reformatted string.
 */
export const NumericField = forwardRef<HTMLInputElement, NumericFieldProps>(
  function NumericField(
    {
      value,
      onChange,
      allowNegative = false,
      decimals = 0,
      grouping = false,
      slotProps,
      ...rest
    },
    forwardedRef,
  ) {
    const input = useRef<HTMLInputElement | null>(null)
    const caret = useRef<number | null>(null)
    const shape = { allowNegative, decimals }
    const display = toDisplayNumber(value, { ...shape, grouping })

    // After React writes the formatted string, put the caret back where the
    // digits say it belongs. Layout effect, so it happens before paint and the
    // cursor is never seen in the wrong place.
    useLayoutEffect(() => {
      const element = input.current
      if (element === null || caret.current === null) return
      const offset = offsetAfterDigits(element.value, caret.current)
      caret.current = null
      element.setSelectionRange(offset, offset)
    })

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const element = event.target
      caret.current = countDigitsBefore(
        element.value,
        element.selectionStart ?? element.value.length,
      )
      onChange(toCanonicalNumber(element.value, shape))
    }

    /**
     * Backspace over a separator should delete the digit, not the decoration.
     *
     * The separator is not something the operator typed, so removing it alone
     * looks to them like the key did nothing — the formatter simply puts it
     * back. Stepping the caret across it first means the browser's own
     * deletion lands on the digit, and every other key keeps native behaviour.
     */
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      // Not event.currentTarget: TextField forwards onKeyDown to its root
      // element, which is the wrapping div and has no selection to move.
      const element = input.current
      if (element === null) return
      const start = element.selectionStart
      const end = element.selectionEnd
      if (start === null || start !== end) return
      const separator = /[٬٫]/
      if (
        event.key === 'Backspace' &&
        start > 0 &&
        separator.test(element.value[start - 1] ?? '')
      ) {
        element.setSelectionRange(start - 1, start - 1)
      } else if (
        event.key === 'Delete' &&
        separator.test(element.value[start] ?? '')
      ) {
        element.setSelectionRange(start + 1, start + 1)
      }
    }

    return (
      <TextField
        {...rest}
        value={display}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          ...slotProps,
          htmlInput: {
            // A text control, so Persian digits and separators can be shown.
            // `numeric` still summons the digits-only keypad on a phone; the
            // signed case needs the fuller keypad to reach the minus.
            inputMode: allowNegative || decimals > 0 ? 'decimal' : 'numeric',
            autoComplete: 'off',
            ...(typeof slotProps?.htmlInput === 'object'
              ? slotProps.htmlInput
              : {}),
            ref: (node: HTMLInputElement | null) => {
              input.current = node
              if (typeof forwardedRef === 'function') forwardedRef(node)
              else if (forwardedRef) forwardedRef.current = node
            },
          },
        }}
      />
    )
  },
)

/** A business integer — a count, a priority, a limit. Persian digits, no grouping. */
export const PersianNumberInput = forwardRef<
  HTMLInputElement,
  Omit<NumericFieldProps, 'grouping'>
>(function PersianNumberInput(props, ref) {
  return <NumericField {...props} grouping={false} ref={ref} />
})

/**
 * An amount of rials. Grouped while editing, never fractional.
 *
 * `decimals` is fixed at zero rather than offered as an option: a fractional
 * rial does not exist, and a field that accepts one would round at submit and
 * move money by the difference.
 */
export const MoneyInput = forwardRef<
  HTMLInputElement,
  Omit<NumericFieldProps, 'grouping' | 'decimals'>
>(function MoneyInput(props, ref) {
  return <NumericField {...props} grouping decimals={0} ref={ref} />
})
